import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mxn, fmtDate, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Select, Input, ConfirmDialog, CreatableSelect, ListSkeleton } from "@/components/Kit";
import { StatusBadge } from "@/components/StatusBadge";
import { PaymentModal } from "@/components/PaymentModal";
import { toast } from "sonner";
import { Banknote, Trash2, FileText, Pencil, Eye } from "lucide-react";

export default function Cobranza() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [remisiones, setRemisiones] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [tab, setTab] = useState("pendientes");
  const [payOpen, setPayOpen] = useState(null);
  const [payType, setPayType] = useState("factura");
  const [banks, setBanks] = useState([]);
  const [editPay, setEditPay] = useState(null);
  const [deletePayOpen, setDeletePayOpen] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [repClient, setRepClient] = useState("");
  const [repOrg, setRepOrg] = useState("");
  const [repMode, setRepMode] = useState("pendiente");
  const [filterClientId, setFilterClientId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/invoices").then((r) => setInvoices(r.data)),
      api.get("/remisiones").then((r) => setRemisiones(r.data)),
      api.get("/payments").then((r) => setPayments(r.data)),
      api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })))),
      api.get("/organizations").then((r) => setOrganizations(r.data)),
      api.get("/payment-banks").then((r) => setBanks(r.data.map((b) => b.name))),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const sortByName = (arr) => [...arr].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));
  const sortedClients = sortByName(clients);
  const sortedOrgs = sortByName(organizations);

  const openReport = () => {
    const p = new URLSearchParams();
    p.set("mode", repMode);
    if (repOrg) p.set("organizacion_id", repOrg);
    else if (repClient) p.set("client_id", repClient);
    window.open(`/reporte-cobranza?${p.toString()}`, "_blank");
    setReportOpen(false);
  };

  // Rows: pending invoices (with grouped related remisiones) + independent pending remisiones
  const pendingRows = [];
  invoices
    .filter((i) => i.balance > 0 && i.status !== "Cancelada" && i.status !== "Anulado")
    .forEach((i) => {
      const related = remisiones.filter((r) => r.invoice_id === i.id);
      pendingRows.push({
        key: `fac-${i.id}`, type: "factura", id: i.id, number: i.number,
        client: i.client, due_date: i.due_date, total: i.total,
        paid: i.amount_paid, balance: i.balance, status: i.status, related,
      });
    });
  remisiones
    .filter((r) => !r.invoiced && r.status !== "Anulado" && !r.anulado && (r.balance ?? r.total) > 0)
    .forEach((r) => {
      pendingRows.push({
        key: `rem-${r.id}`, type: "remision", id: r.id, number: r.number,
        client: r.client, due_date: r.due_date, total: r.total,
        paid: r.amount_paid || 0, balance: r.balance ?? r.total, status: r.status, related: [],
      });
    });

  // Filtro por cliente: solo documentos pendientes del cliente seleccionado.
  const clientsWithPending = (() => {
    const seen = new Map();
    for (const r of pendingRows) if (r.client?.id && !seen.has(r.client.id)) seen.set(r.client.id, r.client);
    return sortByName([...seen.values()]);
  })();
  const filteredRows = filterClientId ? pendingRows.filter((r) => r.client?.id === filterClientId) : pendingRows;

  const totalPending = filteredRows.reduce((s, r) => s + r.balance, 0);
  const totalOverdue = filteredRows.filter((r) => r.status === "Vencida").reduce((s, r) => s + r.balance, 0);
  const collected = (filterClientId ? payments.filter((p) => p.client?.id === filterClientId) : payments).reduce((s, p) => s + p.amount, 0);

  const openPay = (row) => {
    setEditPay(null);
    setPayType(row.type);
    setPayOpen(row);
  };

  const openEditPayment = (p) => {
    const type = p.target_type === "remision" ? "remision" : "factura";
    setEditPay(p);
    setPayType(type);
    setPayOpen({ id: p.invoice_id || p.remision_id, number: p.invoice_number, balance: 0, type });
  };

  const closePay = () => { setPayOpen(null); setEditPay(null); };

  const createBank = async (name) => {
    try {
      await api.post("/payment-banks", { name });
      const r = await api.get("/payment-banks");
      setBanks(r.data.map((b) => b.name));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deletePayment = async () => {
    if (!deletePayOpen) return;
    try {
      await api.delete(`/payments/${deletePayOpen.id}`);
      toast.success("Pago eliminado");
      setDeletePayOpen(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader overline="Ciclo de venta · Paso 4" title="Cobranza"
        action={<Btn onClick={() => setReportOpen(true)} data-testid="cobranza-report-button"><FileText size={16} /> Reportes</Btn>}
      />
      <div className="p-10 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 border border-[#E3E8EE] bg-[#FFFFFF]">
          <Metric label="Por cobrar" value={mxn(totalPending)} big testid="collect-pending" />
          <Metric label="Vencido" value={mxn(totalOverdue)} danger border testid="collect-overdue" />
          <Metric label="Cobrado histórico" value={mxn(collected)} success testid="collect-total" />
        </div>

        <div className="flex gap-1 border-b border-[#E3E8EE]">
          <Tab active={tab === "pendientes"} onClick={() => setTab("pendientes")} testid="tab-pending">
            Cuentas por cobrar ({filteredRows.length})
          </Tab>
          <Tab active={tab === "pagos"} onClick={() => setTab("pagos")} testid="tab-payments">
            Historial de pagos ({payments.length})
          </Tab>
        </div>

        {loading ? (
          <ListSkeleton testid="cobranza-loading" />
        ) : tab === "pendientes" ? (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <label className="flex items-center gap-2">
                <span className="overline">Filtrar por cliente</span>
                <select
                  value={filterClientId}
                  onChange={(e) => setFilterClientId(e.target.value)}
                  data-testid="cobranza-client-filter"
                  className="bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm max-w-[20rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
                >
                  <option value="">Todos los clientes</option>
                  {clientsWithPending.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              {filterClientId && (
                <button onClick={() => setFilterClientId("")} data-testid="cobranza-clear-filter"
                  className="font-mono-data text-xs uppercase tracking-wider text-[#465B7F] border border-[#465B7F]/40 px-3 py-2 hover:bg-[#465B7F]/10 transition-colors">
                  Quitar filtro
                </button>
              )}
            </div>
            {filteredRows.length === 0 ? (
              <p className="font-mono-data text-sm text-[#5C5A55] py-12 text-center">
                {filterClientId ? "Este cliente no tiene saldos pendientes." : "No hay saldos pendientes. Todo cobrado. 🎯"}
              </p>
            ) : (
            <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E3E8EE] text-left">
                    <Th>Documento</Th><Th>Cliente</Th><Th>Vencimiento</Th>
                    <Th className="text-right">Total</Th><Th className="text-right">Cobrado</Th>
                    <Th className="text-right">Saldo</Th><Th>Estatus</Th><Th className="text-right">Acción</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.key} data-testid={`collect-row-${row.number}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors">
                      <Td className="text-[#1C1A17] font-medium">
                        <span className="inline-block px-1.5 py-0.5 mr-2 text-[10px] uppercase tracking-wider rounded-sm border border-[#E3E8EE] text-[#5B6672]">
                          {row.type === "factura" ? "Factura" : "Remisión"}
                        </span>
                        {row.number}
                        {row.related.length > 0 && (
                          <span className="block mt-1 font-mono-data text-xs text-[#5B6672]" data-testid={`collect-related-${row.number}`}>
                            {row.related.length === 1 ? "Remisión " : "Remisiones "}
                            {row.related.map((r) => r.number).join(", ")}
                          </span>
                        )}
                      </Td>
                      <Td>{row.client?.name || "—"}</Td>
                      <Td>{fmtDate(row.due_date)}</Td>
                      <Td className="text-right">{mxn(row.total)}</Td>
                      <Td className="text-right text-[#49AC9A]">{mxn(row.paid)}</Td>
                      <Td className="text-right text-[#ED1F26]">{mxn(row.balance)}</Td>
                      <Td><StatusBadge status={row.status} /></Td>
                      <Td className="text-right whitespace-nowrap">
                        {row.client?.id && (
                          <button onClick={() => navigate(`/clientes/${row.client.id}/estado`)} data-testid={`collect-details-${row.number}`}
                            title="Ver estado de cuenta" className="text-[#5C5A55] hover:text-[#465B7F] mr-4">
                            <Eye size={16} />
                          </button>
                        )}
                        <button onClick={() => openPay(row)} data-testid={`collect-pay-${row.number}`}
                          title="Aplicar pago / Cobrar" className="text-[#5C5A55] hover:text-[#49AC9A]">
                          <Banknote size={16} />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </>
        ) : payments.length === 0 ? (
          <p className="font-mono-data text-sm text-[#5C5A55] py-12 text-center">Aún no hay pagos registrados.</p>
        ) : (
          <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th>Fecha</Th><Th>Documento</Th><Th>Cliente</Th><Th>Método</Th><Th>Banco</Th><Th>Referencia</Th>
                  <Th className="text-right">Monto</Th><Th className="text-right">Acción</Th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors">
                    <Td>{fmtDate(p.date)}</Td>
                    <Td className="text-[#1C1A17]">
                      <span className="inline-block px-1.5 py-0.5 mr-2 text-[10px] uppercase tracking-wider rounded-sm border border-[#E3E8EE] text-[#5B6672]">
                        {p.target_type === "remision" ? "Rem." : "Fac."}
                      </span>
                      {p.invoice_number || "—"}
                    </Td>
                    <Td>{p.client?.name || "—"}</Td>
                    <Td>{p.method}</Td>
                    <Td>{p.bank || "—"}</Td>
                    <Td>{p.reference || "—"}</Td>
                    <Td className="text-right text-[#49AC9A]">{mxn(p.amount)}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <button onClick={() => openEditPayment(p)} className="text-[#5C5A55] hover:text-[#465B7F] mr-4" data-testid={`edit-payment-${p.id}`}>
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setDeletePayOpen(p)} className="text-[#5C5A55] hover:text-[#ED1F26]" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaymentModal
        open={!!payOpen}
        onClose={closePay}
        target={payOpen ? { id: payOpen.id, number: payOpen.number, balance: payOpen.balance, type: payType } : null}
        editPay={editPay}
        banks={banks}
        onCreateBank={createBank}
        onSaved={load}
      />

      <ConfirmDialog
        open={!!deletePayOpen}
        onClose={() => setDeletePayOpen(null)}
        onConfirm={deletePayment}
        title="Eliminar pago"
        message={`¿Seguro que deseas eliminar este pago de ${mxn(deletePayOpen?.amount || 0)}? El saldo de la factura se ajustará automáticamente.`}
        confirmLabel="Eliminar pago"
        testid="confirm-delete-payment-button"
      />

      <Modal open={reportOpen} onClose={() => setReportOpen(false)} title="Reporte de cobranza">
        <div className="space-y-4">
          <Select label="Organización" value={repOrg} onChange={(e) => { setRepOrg(e.target.value); if (e.target.value) setRepClient(""); }} testid="report-org-select">
            <option value="">Todas las organizaciones</option>
            {sortedOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
          <Select label="Cliente" value={repClient} onChange={(e) => { setRepClient(e.target.value); if (e.target.value) setRepOrg(""); }} testid="report-client-select" disabled={!!repOrg}>
            <option value="">General (todos los clientes)</option>
            {sortedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select label="Tipo de reporte" value={repMode} onChange={(e) => setRepMode(e.target.value)} testid="report-mode-select">
            <option value="pendiente">Solo pendiente de cobro (facturas + remisiones)</option>
            <option value="general">General (pagado y pendiente con fechas de pago)</option>
          </Select>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setReportOpen(false)}>Cancelar</Btn>
            <Btn type="button" onClick={openReport} data-testid="generate-report-button">Generar reporte</Btn>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Metric({ label, value, big, danger, success, border, testid }) {
  const color = danger ? "text-[#ED1F26]" : success ? "text-[#49AC9A]" : "text-[#1C1A17]";
  return (
    <div className={`p-8 ${border ? "border-x border-[#E3E8EE]" : ""}`}>
      <div className="overline">{label}</div>
      <div data-testid={testid} className={`font-mono-data font-bold ${big ? "text-4xl" : "text-3xl"} mt-2 ${color}`}>{value}</div>
    </div>
  );
}
function Tab({ children, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 text-sm uppercase tracking-wider border-b-2 -mb-px transition-colors ${active ? "border-[#465B7F] text-[#1C1A17]" : "border-transparent text-[#5C5A55] hover:text-[#1C1A17]"}`}>
      {children}
    </button>
  );
}
function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
